import { useQuery } from '@tanstack/react-query';
import { ArrowRight, X } from 'lucide-react';
import { Card, SectionHeader } from '@/components/ui/card';
import { Pill } from '@/components/ui/pill';
import { Skeleton } from '@/components/ui/feedback';
import { filingErrorSummaryQuery } from '@/lib/queries';
import { fixOwnerLabel } from './status';
import type { FixState } from './record-fix';
import { formatCount } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * The order the fix states are read in, and what each one is called in a sentence about
 * counts rather than on a pill against one record.
 *
 * `none` first and named for the work rather than for the absence of it: a customer counting
 * what is left to do is counting exactly these.
 */
const STATE_ORDER: { state: FixState; label: string }[] = [
  { state: 'none', label: 'need attention' },
  { state: 'fix_failed', label: 'fix refused' },
  { state: 'queued', label: 'queued' },
  { state: 'submitted', label: 'sent' },
  { state: 'corrected', label: 'corrected' },
];

/**
 * "23 with errors" and "16 need attention" are two true numbers that look like a
 * contradiction until something says where the other seven went.
 */
export function errorBreakdownLine(byFixState: Record<string, number>): string | null {
  const parts = STATE_ORDER.filter(({ state }) => (byFixState[state] ?? 0) > 0).map(
    ({ state, label }) => `${formatCount(byFixState[state] ?? 0)} ${label}`,
  );
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * Whether this e-file has anything to explain.
 *
 * `errorCount` alone was the test, and it hid the panel exactly where it was needed most: a
 * transmission the IRS rejects as a whole leaves every record rejected, and a customer was
 * left opening forty drawers to read one sentence forty times.
 *
 * Deliberately loose. The panel renders nothing when there is nothing to group, so asking
 * once too often costs a request and shows no empty card, while asking once too rarely
 * costs somebody an afternoon.
 */
export function shouldExplainFailures(
  filing: { status?: string; errorCount?: number; attentionCount?: number } | undefined,
): boolean {
  if (!filing) return false;
  return (filing.errorCount ?? 0) > 0 || (filing.attentionCount ?? 0) > 0 || filing.status === 'rejected';
}

/**
 * The shape of the reason rows, drawn at their real measurements.
 *
 * A three-by-three table of bars stood here, which is not what arrives: what arrives is a
 * count, a sentence and a pill on a bordered row, so the placeholder changed height as well
 * as shape the moment the answer came back.
 */
function ReasonsSkeleton() {
  return (
    <ul className="flex flex-col gap-2">
      {[0, 1, 2].map((row) => (
        <li key={row}>
          <div className="flex items-start gap-4 rounded-lg border border-[color:var(--ts-border-soft)] px-4 py-3">
            <Skeleton className="mt-1 h-6 w-[3.5rem] shrink-0 rounded-md" />
            <div className="min-w-0 flex-1 pt-1">
              <Skeleton className="h-4 w-[70%] rounded-md" />
              <Skeleton className="mt-2 h-3 w-[30%] rounded-md" />
            </div>
            <Skeleton className="mt-1 h-6 w-20 shrink-0 rounded-full" />
            <Skeleton className="mt-1 h-6 w-24 shrink-0 rounded-md" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Why the records of this e-file failed, largest group first.
 *
 * The alternative is what this page had before: twenty-three errors read one drawer at a
 * time, with no way to tell one problem seen twenty times from twenty separate problems. Most
 * batches are the first, and the first is one decision rather than twenty - so the counts are
 * the point of this panel, and each of them leads to the records it counts.
 */
export function ErrorReasons({
  filingId,
  enabled,
  selectedCode,
  onSelect,
}: {
  filingId: string;
  /** False for an e-file with nothing wrong with it: there is then nothing to ask about. */
  enabled: boolean;
  selectedCode: string | null;
  onSelect: (code: string | null) => void;
}) {
  const summary = useQuery(filingErrorSummaryQuery(filingId, enabled));
  if (!enabled) return null;

  if (summary.isPending) {
    return (
      <Card>
        <SectionHeader title="Why records failed" />
        <ReasonsSkeleton />
      </Card>
    );
  }

  // A summary that cannot be read, or one that groups into nothing, still leaves the panel
  // standing and says so in a line. Returning null here instead made the panel appear while
  // it loaded and then vanish, taking its height with it and moving the page under the
  // reader - and leaving no account of what happened to the errors the header counts.
  // The records table below still says everything this panel says, one record at a time.
  const summaryFailed = summary.isError || !summary.data;
  const { reasons, recordsWithErrors, byFixState } = summary.data ?? {
    reasons: [],
    recordsWithErrors: 0,
    byFixState: {},
  };

  if (summaryFailed || reasons.length === 0) {
    return (
      <Card>
        <SectionHeader title="Why records failed" />
        <p className="text-base text-muted-foreground">
          {summaryFailed
            ? 'These errors could not be grouped just now. Each record below carries its own reason.'
            : 'The IRS did not say why these records failed beyond what each record below carries.'}
        </p>
      </Card>
    );
  }

  const breakdown = errorBreakdownLine(byFixState ?? {});

  return (
    <Card data-tour="error-reasons">
      <SectionHeader
        title="Why records failed"
        description="The largest group first. A record with two problems is counted under both, so the groups can add up to more than the number of records."
      />

      {breakdown ? (
        <p className="-mt-2 pb-5 text-base text-muted-foreground">
          <span className="font-medium text-gray-3">
            {formatCount(recordsWithErrors)} {recordsWithErrors === 1 ? 'record' : 'records'} with errors
          </span>{' '}
          — {breakdown}.
        </p>
      ) : null}

      <ul className="flex flex-col gap-2">
        {reasons.map((reason) => {
          const selected = reason.code !== null && reason.code === selectedCode;
          // A reason the IRS gave no code for cannot be asked about on its own; it is still
          // worth counting, so it is shown and simply does not lead anywhere.
          const selectable = reason.code !== null;
          const owner = fixOwnerLabel[reason.fixOwner] ?? fixOwnerLabel.unknown;
          return (
            <li key={reason.code ?? reason.message}>
              <div
                className={cn(
                  'flex items-start gap-4 rounded-lg border px-4 py-3 transition-colors duration-150 ease-out',
                  selected
                    ? 'border-[color:var(--ts-focus-ring)] bg-[color:var(--ts-row-hover)]'
                    : 'border-[color:var(--ts-border-soft)]',
                )}
              >
                <span className="min-w-[3.5rem] pt-0.5 text-right text-[22px] leading-7 font-semibold tabular-nums text-primary">
                  {formatCount(reason.records)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-base text-gray-3">{reason.message}</p>
                  {/* The IRS's own wording, kept because it is what a customer will be asked
                      to quote if they ever ring the IRS about it. */}
                  {reason.irsMessage && reason.irsMessage !== reason.message ? (
                    <p className="mt-1 text-sm text-muted-foreground">IRS: {reason.irsMessage}</p>
                  ) : null}
                  {reason.code ? (
                    <p className="mt-1 font-mono text-xs text-muted-foreground">{reason.code}</p>
                  ) : null}
                </div>
                <Pill tone={owner.tone}>{owner.label}</Pill>
                {selectable ? (
                  <button
                    type="button"
                    onClick={() => onSelect(selected ? null : reason.code)}
                    className={cn(
                      'inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-base font-medium',
                      'text-primary transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)]',
                      'focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--ts-focus-ring)]',
                    )}
                  >
                    {selected ? (
                      <>
                        Clear
                        <X className="h-4 w-4" aria-hidden="true" />
                      </>
                    ) : (
                      <>
                        Show these
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </>
                    )}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
